<template>
  <v-off-canvas
    id="template-create-offcanvas-v2"
    :backdrop-static="false"
    :offcanvas-class="['offcanvas-end', { 'double-offcanvas': isDoubleOffCanvas }]"
    @closed="$emit('closed')"
  >
    <resizable-container>
      <off-canvas-header>
        <h5 class="offcanvas-title">{{ $t("clientTour.createNewProduct") }} New version</h5>
      </off-canvas-header>

      <off-canvas-body>
        <form id="template-create-form" class="d-flex flex-column justify-content-between h-100">
          <div>
            <fieldset>
              <legend class="visually-hidden">{{ $t("clientTour.basicInformation") }}</legend>

              <div class="search-separator-container">
                <div class="separator-text-container">
                  <span class="separator-text text-sm">{{ $t("clientTour.basicInformation") }}</span>
                </div>
              </div>

              <v-form-input
                id="template-create-name"
                v-model="name"
                :label="$t('general.shared.name')"
                :yup-errors-variable="nameError"
                autocomplete="off"
                form-type="outline"
              />

              <v-form-input
                id="template-create-total-days"
                v-model="totalDays"
                :label="$t('general.shared.totalDays')"
                :yup-errors-variable="totalDaysError"
                autocomplete="off"
                form-type="outline"
                type="number"
              />
            </fieldset>
            <div class="search-separator-container pt-24">
              <div class="separator-text-container">
                <span class="separator-text text-sm">{{ $t("clientTour.howWillYourPath") }}</span>
              </div>
            </div>

            <fieldset>
              <legend class="visually-hidden">{{ $t("clientTour.howWillYourPath") }}</legend>

              <v-form id="template-create-travel-type-select" :label="$t('clientTour.howToDoIt')" form-type="outline">
                <v-select
                  v-model="travelType"
                  :clearable="false"
                  :options="travelTypes"
                  :reduce="(returnValue) => returnValue.key"
                  class="mb-8"
                >
                  <template v-slot:no-options>
                    <div class="my-4">{{ $t("general.vueSelect.noOptions") }}</div>
                  </template>
                </v-select>
              </v-form>

              <v-input-address
                id="template-create-origin-search"
                ref="template-create-origin-search-ref"
                :get-address="true"
                :get-radius-bounds="true"
                :label="$t('general.shared.origin')"
                :radius-bounds="20000"
                class="col-12"
                @address="createLocation($event, true)"
              />

              <v-input-address
                id="template-create-destination-search"
                ref="template-create-destination-search-ref"
                :get-address="true"
                :get-radius-bounds="true"
                :label="$t('general.shared.destination')"
                :radius-bounds="20000"
                class="col-12"
                @address="createLocation($event, false)"
              />
              
              <div :class="['btn-icon', 'mt-20', 'action-btn', 'm-5', { 'checked-class': isPublished }]">
                <v-checkbox
                  id="is-published"
                  v-model="isPublished"
                  :for-key="`is-published`"
                  :label="$t('general.shared.published')"
                  class="text-nowrap col-12"
                />
              </div>
            </fieldset>
          </div>
          <fieldset>
            <legend class="visually-hidden">{{ $t("general.shared.media") }}</legend>

            <div class="resource-media-container mt-12">
              <div class="media-title text-lg fw-semi mb-16">{{ $t("general.shared.media") }}</div>

              <v-uploader ref="uploader" :after-upload="onChange" route-prefix="api/s3" @removed="onChange" />
            </div>
          </fieldset>

          <v-button
            :disabled="!meta.valid || disableButton"
            :is-loading="accepted"
            class="btn-icon w-fit align-self-end mt-24"
            size="sm"
            variant="primary"
            @click="createTemplate"
          >
            {{ $t("general.button.create") }}
            <v-icon icon="arrow-right" size="sm" space="ms-12" />
          </v-button>
        </form>
      </off-canvas-body>
    </resizable-container>
  </v-off-canvas>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import * as yup from "yup";
import { useField } from "vee-validate";
import { VForm, VFormInput, VCheckbox } from "@uniqoders/form";
import { useI18n } from "vue-i18n";
import vSelect from "vue-select";
import VOffCanvas from "@/components/vendor/basic/offcanvas/VOffCanvas.vue";
import OffCanvasHeader from "@/components/vendor/basic/offcanvas/OffCanvasHeader.vue";
import OffCanvasBody from "@/components/vendor/basic/offcanvas/OffCanvasBody.vue";
import useFormValidation from "@/helpers/form";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import { useAuthStore } from "@/stores/auth";
import api from "@/api";
import ClientTour from "@/api/objects/ClientTour";
import Template from "@/api/objects/Template";
import { mapConcepts } from "@/helpers/ConceptHelper";
import useConcepts from "@/helpers/Concepts";
import VInputAddress from "@/components/vendor/basic/form/VInputAddress.vue";
import Location from "@/api/objects/Location";
import ResizableContainer from "@/components/resizablecontainer/ResizableContainer.vue";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import VUploader from "@/components/vendor/upload/VUploader.vue";

const props = defineProps({
  templateData: {
    type: Object as () => Template, // Ensures proper typing
    required: false,
    default: null,
  },
  clientTourData: {
    type: Object as () => ClientTour, // Ensures proper typing
    required: false,
    default: null,
  },
  isDoubleOffCanvas: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const emit = defineEmits(["closed", "reloadTemplates"]);

const router = useRouter();
const { t } = useI18n();
const toast = useToast();

const rules = yup.object({
  name: yup.string().required().label(t("general.shared.name")),
  totalDays: yup
    .number()
    .required()
    .min(1)
    .transform((value) => (isNaN(value) ? undefined : value))
    .label(t("general.shared.daysNumber")),
  travelType: yup.string().required().label(t("general.shared.client")),
});

const initialValues = {
  totalDays: 1,
};

// Form Validation
const form = useFormValidation(rules, initialValues);
const { value: name, errorMessage: nameError } = useField<string>("name");
const { value: totalDays, errorMessage: totalDaysError } = useField<number>("totalDays");
const { value: travelType, errorMessage: travelTypeError } = useField<string>("travelType");
const { accepted, meta, toggleAccepted } = form;

const concepts = useConcepts();
const travelTypes = computed(() => {
  return concepts.concepts.value["tour.travel_type"] ? mapConcepts(concepts.concepts.value["tour.travel_type"]) : [];
});

const authStore = useAuthStore();
const disableButton = ref(false);
const origin = ref(null);
const destination = ref(null);
const isPublished = ref(false);
const uploader = ref(null);
const media = ref([]);

const mediaUuids = computed(() => {
  return media.value.map((mediaItem: any) => ({
    uuid: mediaItem.attributes.uuid,
    name: mediaItem.attributes.name,
    collection: "activity_image",
  }));
});

const onChange = () => {
  if (uploader.value) {
    media.value = uploader.value.mediaLibrary.state.media;
  }
};

const createLocation = async (address: Record<string, any>, isOrigin = true) => {
  try {
    const location: Location = await api.location.create({
      name: address.name,
      provider_id: address.placeId,
      lat: address.lat,
      lng: address.lng,
      country: address.country.longName,
      province: address.regionProvince.longName,
      state: address.stateCommunity.longName,
    });

    if (isOrigin) {
      origin.value = location.id;
    } else {
      destination.value = location.id;
    }
  } catch (error) {
    console.error("Error creating location:", error);
  }
};

const createTemplate = async () => {
  try {
    toggleAccepted();
    disableButton.value = true;

    const data: any = {
      name: name.value, // Ensure `.value` is used
      total_days: totalDays.value, // Ensure `.value` is used
      travel_type: travelType.value, // Ensure `.value` is used
      origin: origin.value,
      destination: destination.value,
      is_published: isPublished.value,
    };

    if (authStore.agency) {
      const response = await api.activityService.createEmptyActivity(authStore.agency.slug, data);

      if (response && !!mediaUuids.value && mediaUuids.value.length > 0) {
        const payload = {
          media: mediaUuids.value,
          model_type: "activity",
          collection: "activity_image",
          model_id: response.id,
        };

        await api.media.confirm("s3", payload);
      }

      toast.success(t("general.shared.savedChanges"), {
        position: "bottom-right",
      });
      emit("reloadTemplates");
      await router.replace({
        name: "activities.show",
        params: { activitiesId: response.id },
      });
    }
  } catch (e: any) {
    console.error(e);
    toast.error(e.response.data.message, {
      position: "bottom-right",
    });
  } finally {
    toggleAccepted();
    disableButton.value = false;
  }
};
</script>

<style scoped>
.action-btn {
  font-size: 1rem;
  border: 1px solid #000;
  padding: 10px 15px;
  border-radius: 10px;
}

.checked-class {
  border: 1px solid #ee2a7b;
}

.action-btn .icon {
  background-color: transparent;
  margin-right: 7px;
  display: flex;
}
</style>
